'use client';

import React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import type { Locale } from '@/i18n/config';
import type { RoomNameCatalogEntry } from '@/lib/rooms/catalog';
import { normalizeRoomName } from '@/lib/rooms/names';
import { buildMeetingPath } from '@/lib/rooms/short-url';
import {
  loadRecentMeetings,
  removeRecentMeeting,
  type RecentMeetingRecord,
} from '@/lib/rooms/recent-meetings';
import { RoomNameWithDescription } from '@/lib/rooms/RoomNameWithDescription';
import styles from '@/styles/Home.module.css';

export default function HomeClient({ catalog }: { catalog: RoomNameCatalogEntry[] }) {
  const router = useRouter();
  const t = useTranslations('home');
  const locale = useLocale() as Locale;
  const [recent, setRecent] = React.useState<RecentMeetingRecord[]>([]);
  const [roomStatuses, setRoomStatuses] = React.useState<Record<string, 'active' | 'closed'>>({});
  React.useEffect(() => {
    setRecent(loadRecentMeetings());
  }, []);
  React.useEffect(() => {
    if (recent.length === 0) return;
    const query = recent.map((item) => `room=${encodeURIComponent(item.shortCode)}`).join('&');
    fetch(`/api/rooms/status?${query}`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((data: { rooms?: { key: string; status: 'active' | 'closed' }[] } | undefined) => {
        if (!data?.rooms) return;
        setRoomStatuses(
          Object.fromEntries(data.rooms.map((room) => [room.key, room.status])) as Record<
            string,
            'active' | 'closed'
          >,
        );
      })
      .catch(() => undefined);
  }, [recent]);
  const descriptions = React.useMemo(
    () => new Map(catalog.map((entry) => [entry.key, entry])),
    [catalog],
  );
  const open = (item: RecentMeetingRecord) => {
    const href = buildMeetingPath(item.shortCode);
    if (href) router.push(href);
  };
  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <Image
            className={styles.homeLogo}
            src="/images/nexo.png"
            alt="Nexo Meeting"
            width="1536"
            height="1024"
          />
        </div>
        <section className={styles.meetingActions} aria-label={t('recentMeetings')}>
          <h2>{t('recentMeetings')}</h2>
          <p>{t('inviteHint')}</p>
          {recent.length === 0 ? (
            <p>{t('noRecentMeetings')}</p>
          ) : (
            <ul>
              {recent.map((item) => {
                const entry = descriptions.get(normalizeRoomName(item.roomName));
                return (
                  <li key={item.roomName}>
                    <div className={styles.recentMeetingRow}>
                      <div>
                        <RoomNameWithDescription
                          name={item.roomName}
                          description={entry?.descriptions[locale]}
                        />
                        <span className={styles.roomStatus}>
                          {t(roomStatuses[item.shortCode] === 'active' ? 'inUse' : 'closed')}
                        </span>
                        <time dateTime={item.joinedAt}>
                          {new Date(item.joinedAt).toLocaleString()}
                        </time>
                      </div>
                      <button type="button" onClick={() => open(item)}>
                        {t('joinMeeting')}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRecent(removeRecentMeeting(item.shortCode))}
                    >
                      {t('removeRecentMeeting')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
      <footer data-lk-theme="default">Copyright 2026 Xmap</footer>
    </>
  );
}
